import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { PropsWithChildren } from 'react';
import { Label } from 'semantic-ui-react';

import { EPSSPopup } from '@components/epss-popup';
import { KEVIndicator } from '@components/kev-indicator';
import type { SeverityLabelProps } from '@components/severity';
import { SeverityLabel } from '@components/severity';

import s from './vulnerability-indicators.scss';

export interface VulnerabilityIndicatorsProps {
  className?: string;
  cvssAssessment?: SeverityLabelProps['cvssAssessment'];
  epssPercentile?: number | null;
  epssScore?: number | null;
  kev?: boolean;
  showLabels?: boolean;
  vulnerabilityId?: string | null;
}

const Indicator = ({
  children,
  label,
}: PropsWithChildren<{ label?: string }>) => (
  <span className={s.indicator}>
    {label && <span className={s.label}>{label}</span>}
    {children}
  </span>
);

/**
 * Compact per-vulnerability indicator cluster — the CVSS badge, EPSS score, and
 * KEV flag rendered inline in the canonical CVSS → EPSS → KEV order.
 */
export const VulnerabilityIndicators = ({
  className,
  cvssAssessment,
  epssPercentile = null,
  epssScore = null,
  kev = false,
  showLabels = true,
  vulnerabilityId,
}: VulnerabilityIndicatorsProps) => {
  useStyles(s);

  return (
    <div className={classNames(s.root, className)}>
      <Indicator label={showLabels ? 'CVSS' : undefined}>
        <SeverityLabel
          cvssAssessment={cvssAssessment}
          variant="enhanced"
          vulnerabilityId={vulnerabilityId}
        />
      </Indicator>

      <Indicator label={showLabels ? 'EPSS' : undefined}>
        <EPSSPopup percentile={epssPercentile} score={epssScore} />
      </Indicator>

      <Indicator label={showLabels ? 'KEV' : undefined}>
        <Label className={s.kevLabel}>
          <KEVIndicator value={kev} />
        </Label>
      </Indicator>
    </div>
  );
};

export default VulnerabilityIndicators;
